
import { TitleHeading } from "@/components/helper/TitleHeading";
import "../../styles/contactForm.css";
import { FormEvent, useState, useMemo, useEffect, useRef } from "react";
import { BaseSection, BaseSectionProps } from "@/components/sections/BaseSection";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FiChevronDown, FiClock, FiGlobe, FiMail } from "react-icons/fi";
import { FaTelegram } from "react-icons/fa";
import { safeTrack } from "@/utils/analytics";
import { isRateLimited, remainingCooldownMs, safeGetItem, safeSetItem } from "@/utils/rate-limit";
import { isTimeoutError, withTimeout } from "@/utils/fetch-with-timeout";
import { configuredValue } from "@/utils/env-config";
import { SUBMIT_COOLDOWN_SECONDS, beginSubmit, supersede, type PendingSubmit, type SubmitStatus } from "@/utils/submit-status";
import { getSocialMediaLink } from "@/utils/external-links";

// Animation variants for consistent reuse
const animationVariants = {
    container: {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    },
    item: {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    },
    slideLeft: {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    },
    button: {
        hidden: { opacity: 0, scale: 0.9 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    },
    success: {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    }
};

// Reduced motion variants for accessibility
const reducedMotionVariants = {
    container: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    },
    item: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    },
    slideLeft: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    },
    button: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    },
    success: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    }
};

// Web3Forms API endpoint
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
// Replace with your Web3Forms access key from https://web3forms.com
// Trimmed and placeholder-checked, not merely non-empty: a key left at the
// documented placeholder produces a form that fails at every submit, which is
// worse for the visitor than the direct-contact fallback.
const RAW_ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;
// `configuredValue` hands back the TRIMMED value, so there is no padded form
// left in scope for a later edit to reach for by mistake.
const WEB3FORMS_ACCESS_KEY = configuredValue(RAW_ACCESS_KEY) ?? "";

if (!WEB3FORMS_ACCESS_KEY) {
    // Logged unconditionally, not only in DEV: the build gate in
    // scripts/check-env.ts is skippable (a host overriding the build command, a
    // fork, a direct `vite build`), and without a signal here a production site
    // would quietly serve the direct-contact fallback forever with nobody
    // alerted. An empty key makes every submission fail at Web3Forms with only
    // the generic user-facing error, hiding the real cause.
    //
    // The two causes are named separately: an operator told only "missing"
    // checks the host, sees the variable set to a placeholder, and disbelieves
    // the log - the diagnostic points away from the real cause.
    console.error(
        RAW_ACCESS_KEY && RAW_ACCESS_KEY.trim()
            ? "VITE_WEB3FORMS_ACCESS_KEY is set to a documented placeholder rather than a real key — the contact form is disabled and the direct-contact fallback is shown instead."
            : "VITE_WEB3FORMS_ACCESS_KEY is missing — the contact form is disabled and the direct-contact fallback is shown instead."
    );
}

/**
 * Web3Forms signals the outcome with a boolean `success` field. The payload is
 * an untrusted network response, so it is parsed as `unknown` and narrowed here
 * rather than cast: a JSON `null` would throw on property access, and a truthy
 * non-boolean (`"false"`) would read as a successful send.
 */
function isSuccessPayload(body: unknown): boolean {
    return typeof body === 'object' && body !== null && (body as { success?: unknown }).success === true;
}

const RATE_LIMIT_SECONDS = SUBMIT_COOLDOWN_SECONDS;
const RATE_LIMIT_STORAGE_KEY = 'contactForm_lastSubmit';

export default function ContactForm(props: BaseSectionProps) {
    // Every outcome, including the rate-limit lockout, is one member of a single
    // closed union. `rateLimited` used to be a separate boolean, which made
    // ('success' + rateLimited) representable and rendered two banners at once;
    // the only thing preventing it was the order of two setState calls in
    // onSubmit, an invariant the auto-clear effect below never consulted.
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
    const { t } = useTranslation();

    // Holds the in-flight submission so unmount can abort it, and so a
    // superseded attempt can tell itself apart from an abandoned one.
    const abortRef = useRef<PendingSubmit | null>(null);

    // Abort any in-flight submission when the component unmounts so a stalled
    // request neither leaks nor tries to set state after teardown.
    useEffect(() => {
        return () => abortRef.current?.controller.abort();
    }, []);

    // Extract inView state for cleaner code
    const isInView = props.section.viewHook.inView;

    // Check for reduced motion preference - memoize to prevent re-renders
    const prefersReducedMotion = useMemo(() => {
        return typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }, []);

    // Select appropriate variants based on user preference
    const variants = prefersReducedMotion ? reducedMotionVariants : animationVariants;

    // With no Web3Forms key the form can only ever fail at submit. Render a
    // direct-contact fallback (email + Telegram) instead of a doomed form.
    const contactConfigured = Boolean(WEB3FORMS_ACCESS_KEY);
    const emailAddress = getSocialMediaLink('email');
    const telegramLink = getSocialMediaLink('telegram');

    // Make the degraded state measurable in production. Without this a build
    // that slipped past the env gate looks identical in analytics to a section
    // nobody scrolled to.
    useEffect(() => {
        if (!contactConfigured) safeTrack('contact_form_unconfigured');
    }, [contactConfigured]);

    // Auto-clear the rate-limit lockout once the cooldown window elapses.
    // Without it the submit button stays disabled until a page reload.
    const isRateLimitedStatus = submitStatus === 'rate-limited';
    useEffect(() => {
        if (!isRateLimitedStatus) return;
        const lastSubmit = safeGetItem(RATE_LIMIT_STORAGE_KEY);
        const remainingMs = remainingCooldownMs(lastSubmit, Date.now(), RATE_LIMIT_SECONDS);
        if (remainingMs <= 0) {
            setSubmitStatus('idle');
            return;
        }
        const timer = setTimeout(() => setSubmitStatus('idle'), remainingMs);
        return () => clearTimeout(timer);
    }, [isRateLimitedStatus]);

    async function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        // React nulls currentTarget after the synchronous handler returns —
        // capture the form element before any await.
        const form = event.currentTarget;

        // Honeypot: bots fill hidden fields, humans never do. Silently accept.
        const honeypot = (form.elements.namedItem('website') as HTMLInputElement | null)?.value;
        if (honeypot) {
            setSubmitStatus('success');
            form.reset();
            return;
        }

        // Client-side rate limit to slow down repeat/bot submissions, and - after
        // a timeout - to stop a resend duplicating a message the provider may
        // already have accepted. Moving to the 'rate-limited' status replaces any
        // stale banner by construction, so exactly one notice can be on screen.
        const lastSubmit = safeGetItem(RATE_LIMIT_STORAGE_KEY);
        if (isRateLimited(lastSubmit, Date.now(), RATE_LIMIT_SECONDS)) {
            setSubmitStatus('rate-limited');
            return;
        }

        setSubmitStatus('submitting');
        safeTrack('contact_form_submit');

        const formData = new FormData(form);
        formData.append("access_key", WEB3FORMS_ACCESS_KEY);
        formData.append("from_name", "Portfolio Contact Form");

        function fail() {
            setSubmitStatus('error');
            safeTrack('contact_form_error');
        }

        // Supersede any earlier submission still in flight, so exactly one
        // controller is live and unmount can always cancel it. Tagged, so the
        // superseded attempt's catch can tell itself apart from an unmount.
        supersede(abortRef.current);
        const pending = beginSubmit();
        abortRef.current = pending;

        let result: unknown;
        // Set when the provider answered 2xx and the body could not be parsed.
        // That is the ambiguous case: Web3Forms may already have accepted and
        // sent the message, so the resend has to be held off exactly as it is
        // after a deadline breach.
        let unreadableSuccessBody = false;
        try {
            // The body read lives inside the deadline: `fetch` resolves on
            // headers alone, so a 200 followed by a stalled body would
            // otherwise leave the button disabled forever.
            result = await withTimeout(async (signal) => {
                const response = await fetch(WEB3FORMS_ENDPOINT, {
                    method: "POST",
                    body: formData,
                    signal
                });
                if (!response.ok) {
                    // Web3Forms puts the actionable reason in the body of a 4xx
                    // - a revoked key, a blocked domain - and that is exactly
                    // the failure the env gate cannot catch, because the key is
                    // present and not a placeholder, just wrong. A bare status
                    // code leaves the operator unable to tell that from a
                    // malformed payload.
                    const detail = await response.text().catch(() => "");
                    throw new Error(
                        `Web3Forms HTTP error: ${response.status} ${detail.slice(0, 200)}`.trim(),
                    );
                }
                // Read as text and parsed here rather than via `response.json()`
                // so a 2xx whose body will NOT parse - a proxy/CDN interstitial,
                // a captive portal - is distinguishable in the catch from a
                // request that never reached Web3Forms at all. The two need
                // different handling: the second sent nothing, the first may
                // well have been accepted and emailed.
                const raw = await response.text();
                try {
                    // Deliberately `unknown`: this is whatever the endpoint
                    // chose to send. It is narrowed by `isSuccessPayload` below.
                    const body: unknown = JSON.parse(raw);
                    return body;
                } catch {
                    unreadableSuccessBody = true;
                    throw new Error(
                        `Web3Forms returned an unreadable ${response.status} body ` +
                        `(content-type ${response.headers.get("content-type") ?? "none"})`,
                    );
                }
            }, { signal: pending.controller.signal });
        } catch (error) {
            // Silence is keyed on OUR controller, not on the error's shape: it
            // is aborted exactly when this component unmounted or a newer
            // submit superseded this attempt, which are the only two cases
            // where there is nobody left to show a banner to. An AbortError
            // from anywhere else has to surface, or the button stays disabled
            // forever with no explanation.
            if (pending.controller.signal.aborted) {
                if (pending.superseded) {
                    // Still on screen, and a newer attempt owns the outcome -
                    // nothing to count. The discarded error may still be the
                    // real diagnostic, and nothing else would ever print it.
                    console.debug("Contact form superseded attempt failed:", error);
                    return;
                }
                // Unmount: the visitor navigated away inside the deadline and
                // the POST was cancelled, so the message is lost. There is
                // nobody left to show a banner to, but without this the funnel
                // records a `contact_form_submit` with NO terminal event -
                // unattributable rather than merely uncounted.
                safeTrack('contact_form_abandoned');
                return;
            }
            console.error("Contact form submit failed:", error);
            // A deadline breach is retryable and gets its own copy; an HTTP
            // error, a non-JSON body and any other network failure share the
            // generic banner. Both re-enable the button.
            if (isTimeoutError(error)) {
                setSubmitStatus('timeout');
                safeTrack('contact_form_timeout');
                // A deadline breach says nothing about whether Web3Forms
                // received the message - the request may well have completed
                // server-side, so an immediate resend would duplicate it. The
                // timestamp starts the cooldown WITHOUT setting `rateLimited`:
                // the button stays enabled (a stuck button is the failure this
                // whole change exists to remove) and the copy asks the user to
                // wait, but a resend inside the window is caught by the normal
                // rate-limit path rather than sent twice.
                safeSetItem(RATE_LIMIT_STORAGE_KEY, Date.now().toString());
                return;
            }
            if (unreadableSuccessBody) {
                // Same rule the newsletter applies to `non-json` / `unparseable`
                // / `empty-body`: a 2xx we could not read says nothing about
                // whether the provider accepted the message. Without the
                // cooldown the banner invites a click that delivers it twice.
                safeSetItem(RATE_LIMIT_STORAGE_KEY, Date.now().toString());
            }
            fail();
            return;
        }

        if (isSuccessPayload(result)) {
            setSubmitStatus('success');
            safeSetItem(RATE_LIMIT_STORAGE_KEY, Date.now().toString());
            safeTrack('contact_form_success');
            form.reset();
        } else {
            // A 2xx that UNDERSTOOD the request and refused it: a revoked or
            // rotated key, an unverified domain, an exhausted quota. Retrying
            // cannot fix any of those, so the generic "try again" banner sends
            // the visitor round a loop that fails identically forever while the
            // operator sees only `contact_form_error` - indistinguishable from a
            // transient blip, on a channel that is permanently dead. The
            // provider's own reason is logged with it; the newsletter separates
            // exactly this case already.
            console.error("Web3Forms refused the submission:", result);
            setSubmitStatus('rejected');
            safeTrack('contact_form_rejected');
            // Fired for a rejection too, not instead of it, matching the rule
            // the newsletter states for `newsletter_signup_rejected`: the
            // message was not delivered either way, so the generic failure rate
            // must not quietly improve just because the failure was
            // attributable. Tracking only the specific event would leave the
            // aggregate `contact_form_error` dashboard flat at zero while every
            // submission dies at the provider - the same blind spot in a new
            // place, which is the opposite of what separating the case was for.
            safeTrack('contact_form_error');
            // No cooldown: nothing was sent, and nothing the visitor does to the
            // form will change that - they need the fallback, not a wait.
        }
    }

    const isSubmitting = submitStatus === 'submitting';
    const isSuccess = submitStatus === 'success';
    const isTimeout = submitStatus === 'timeout';
    const isRejected = submitStatus === 'rejected';
    const isError = submitStatus === 'error' || isTimeout || isRejected;
    const rateLimited = isRateLimitedStatus;

    return (
        <BaseSection {...props}>
            <div className="contact-grid">
            <motion.div
                className="contact-info"
                variants={variants.container}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                style={{
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)'
                }}
            >
                <div className="w-full">
                    <motion.div variants={variants.item}>
                        <TitleHeading className="title-heading mb-3">
                            {t("contact.title")}
                        </TitleHeading>
                    </motion.div>
                    <motion.h2 className="section-subtitle" variants={variants.item}>
                        {t("contact.connect")}
                    </motion.h2>
                    <motion.p className="section-lede" variants={variants.item}>
                        {t("contact.description")}
                    </motion.p>
                    <motion.div
                        className="contact-links"
                        variants={variants.item}
                    >
                        <a
                            href="mailto:0xnicholasy.eth@gmail.com"
                            className="contact-email-link contact-link"
                        >
                            <FiMail className="size-4" />
                            <span>0xnicholasy.eth@gmail.com</span>
                        </a>
                        <div className="contact-link contact-meta">
                            <FiGlobe className="size-4" />
                            <span>
                                {t("contact.timezone")}
                            </span>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {contactConfigured ? (
            <motion.div
                className="contact-panel"
                variants={variants.container}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                style={{
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)'
                }}
            >
                <div className="contact-form">
                    <motion.form
                        method="post"
                        id="contactform"
                        onSubmit={onSubmit}
                        onInput={() => {
                            // Re-enable the form once the user starts a new message after a
                            // completed attempt, so a second legitimate inquiry doesn't require
                            // a full page reload.
                            if (isSuccess || isError) setSubmitStatus('idle');
                        }}
                        variants={variants.item}
                    >
                        <motion.div
                            className="contact-name-row"
                            variants={variants.slideLeft}
                        >
                            <div>
                                <label htmlFor="name" className="sr-only">{t("contact.name")}</label>
                                <input className="w-full" type="text" id="name" name="name" placeholder={t("contact.name")}
                                    required />
                            </div>
                            <div>
                                <label htmlFor="email" className="sr-only">{t("contact.email")}</label>
                                <input className="w-full" type="email" id="email" name="email" placeholder={t("contact.email")}
                                    required />
                            </div>
                        </motion.div>
                        <label htmlFor="reasonForContact" className="sr-only">{t("contact.reasonForContact")}</label>
                        <motion.div className="contact-select-wrap" variants={variants.slideLeft}>
                            <select
                                id="reasonForContact"
                                name="reasonForContact"
                                required
                                defaultValue=""
                            >
                                <option value="" disabled>{t("contact.reasonForContactPlaceholder")}</option>
                                <option value="hiring">{t("contact.reasonForContactHiring")}</option>
                                <option value="coaching">{t("contact.reasonForContactCoaching")}</option>
                                <option value="freelance">{t("contact.reasonForContactFreelance")}</option>
                                <option value="other">{t("contact.reasonForContactOther")}</option>
                            </select>
                            <FiChevronDown aria-hidden="true" className="contact-select-icon" />
                        </motion.div>
                        <label htmlFor="subject" className="sr-only">{t("contact.subject")}</label>
                        <motion.input
                            className="row"
                            type="text"
                            id="subject"
                            name="subject"
                            placeholder={t("contact.subject")}
                            required
                            variants={variants.slideLeft}
                        />
                        <label htmlFor="message" className="sr-only">{t("contact.message")}</label>
                        <motion.textarea
                            name="message"
                            id="message"
                            placeholder={t("contact.message")}
                            required
                            variants={variants.slideLeft}
                        />
                        <input
                            type="text"
                            name="website"
                            id="website"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                        />
                        <motion.div
                            className="contact-actions"
                            variants={variants.item}
                        >
                            <div className="hidden sm:inline-flex contact-link contact-meta self-start md:self-auto">
                                <FiClock className="size-4" />
                                <span>
                                    {t("contact.responseTime")}
                                </span>
                            </div>
                            <motion.button
                                className="button button-md button-dark contact-submit-btn"
                                type="submit"
                                disabled={isSubmitting || isSuccess || rateLimited}
                                variants={variants.button}
                                whileHover={!prefersReducedMotion && !isSubmitting ? {
                                    scale: 1.05,
                                    transition: { duration: 0.2 }
                                } : {}}
                                whileTap={!prefersReducedMotion && !isSubmitting ? { scale: 0.95 } : {}}
                            >
                                <p className="text-secondary">
                                    {isSubmitting ? t("contact.sending") : t("contact.send")}
                                </p>
                            </motion.button>
                        </motion.div>
                    </motion.form>
                    {rateLimited && (
                        <motion.div
                            className="mt-4 lg:mt-8 text-right text-yellow-600 dark:text-yellow-400"
                            variants={variants.success}
                            initial="hidden"
                            animate="visible"
                            role="status"
                            aria-live="polite"
                        >
                            <span>{t("contact.rateLimited")}</span>
                        </motion.div>
                    )}
                    {isSuccess && (
                        <motion.div
                            className="mt-4 lg:mt-8 text-right text-green-600 dark:text-green-400"
                            variants={variants.success}
                            initial="hidden"
                            animate="visible"
                            role="status"
                            aria-live="polite"
                        >
                            <span>{t("contact.success")}</span>
                        </motion.div>
                    )}
                    {isError && (
                        <motion.div
                            className="mt-4 lg:mt-8 text-right text-red-600 dark:text-red-400"
                            variants={variants.success}
                            initial="hidden"
                            animate="visible"
                            role="alert"
                            aria-live="assertive"
                        >
                            <span>
                                {isTimeout
                                    ? t("contact.timeoutError")
                                    : isRejected
                                        ? t("contact.rejected")
                                        : t("contact.error")}
                            </span>
                        </motion.div>
                    )}
                </div>
            </motion.div>
            ) : (
            <motion.div
                className="contact-panel"
                variants={variants.container}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                style={{
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)'
                }}
            >
                <motion.div
                    className="contact-fallback flex flex-col gap-3 rounded-xl border border-theme-primary/20 bg-theme-background p-5"
                    variants={variants.item}
                >
                    <p className="text-sm text-theme-primary">
                        {t("contact.reachDirectly")}
                    </p>
                    <a
                        href={`mailto:${emailAddress}`}
                        className="inline-flex items-center gap-2 font-semibold text-theme-primary hover:underline"
                    >
                        <FiMail className="size-4" />
                        <span>{emailAddress}</span>
                    </a>
                    <a
                        href={telegramLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-semibold text-theme-primary hover:underline"
                    >
                        <FaTelegram className="size-4" />
                        <span>Telegram</span>
                    </a>
                </motion.div>
            </motion.div>
            )}
            </div>
        </BaseSection>
    );
}
